import { Optional } from '@app/shared/data-types/optional.types'
import { IStreamThreadActionRoot, IStreamThreadInfo } from '@app/stream-module/core/data-types/stream.interface'
import {
  ITask,
  ITaskBlocksSortingItem,
  ITaskSubtask,
  ITaskSubtaskGroup,
  TaskPriorityTypes,
} from '@app/tasks-module/core/data-types/task.interface'
import { ITextBlock } from '@app/tasks-module/core/data-types/text-block.interface'
import { IProject } from '@app/projects-module/core/data-types/project.interface'
import { IProjectRole } from '@app/project-roles/core/data-types/project-role.interface'
import { IBlockingSource } from '@app/daily-module/core/data-types/blocking.types'
import { IFile } from '@app/tasks-module/core/data-types/task-file.interface'
import { UploadVersionType } from '@app/recorder-module/core/types/upload'
import { ChatStatus, ChatMessageSide, IAdminChatMessage, IChatBlock } from '@app/support-module/core/types'

export enum WsMessageEventTypes {
  CREATE_TASK = 'pm.task.created',
  DELETE_TASK = 'pm.task.deleted',
  UPDATE_TASK_TITLE = 'pm.task.title_changed',
  UPDATE_TASK_OWNER = 'pm.task.owner_changed',
  UPDATE_TASK_DURATION = 'pm.task.dates_changed',
  UPDATE_TASK_POINT = 'pm.task.point_estimate_changed',
  UPDATE_TASK_PRIORITY = 'pm.task.priority_changed',
  UPDATE_TASK_STAGE = 'pm.task.stage.changed',
  ADD_TASK_TAG = 'pm.task.tag_added',
  DELETE_TASK_TAG = 'pm.task.tag_removed',
  UPDATE_TASK_TAG = 'pm.task_tag.changed',
  ADD_TASK_SUBSCRIBER = 'pm.task.subscriber.added',
  DELETE_TASK_SUBSCRIBER = 'pm.task.subscriber.removed',
  REPLACE_TASK_SUBSCRIBER = 'pm.task.subscriber.replaced',
  TASK_BLOCKS_SORTED = 'pm.task.task_blocks.sorted',

  CREATE_TASK_SUBTASK_GROUP = 'pm.task.subtask_group.created',
  DELETE_TASK_SUBTASK_GROUP = 'pm.task.subtask_group.deleted',
  RENAME_TASK_SUBTASK_GROUP = 'pm.task.subtask_group.renamed',

  CREATE_TASK_TEXT_BLOCK = 'pm.task.text_block.created',
  DELETE_TASK_TEXT_BLOCK = 'pm.task.text_block.deleted',
  UPDATE_TASK_TEXT_BLOCK = 'pm.task.text_block.changed',
  CREATE_TASK_TEXT_BLOCKS = 'pm.task.text_blocks.created',

  CREATE_TASK_SUBTASK = 'pm.task.subtask_group.subtask.created',
  DELETE_TASK_SUBTASK = 'pm.task.subtask_group.subtask.deleted',
  UPDATE_TASK_SUBTASK_ASSIGNEE = 'pm.task.subtask_group.subtask.assignee.changed',
  UPDATE_TASK_SUBTASK_TITLE = 'pm.task.subtask_group.subtask.title_changed',
  UPDATE_TASK_SUBTASK_STATUS = 'pm.task.subtask_group.subtask.state_changed',
  CHANGE_GROUP_TASK_SUBTASK = 'pm.task.subtask_group.subtask.group_changed',
  BLOCKED_TASK_SUBTASK = 'pm.task.subtask_group.subtask.blocked',
  UNBLOCKED_TASK_SUBTASK = 'pm.task.subtask_group.subtask.unblocked',

  UPDATE_STREAM = 'stream.message_thread.actions_upserted',

  CREATE_PROJECT = 'pm.project.created',
  UPDATE_PROJECT = 'pm.project.updated',
  DELETE_PROJECT = 'pm.project.deleted',

  CREATE_PROJECT_ROLE = 'pm.project_role.created',
  UPDATE_PROJECT_ROLE = 'pm.project_role.updated',
  DELETE_PROJECT_ROLE = 'pm.project_role.deleted',

  CREATE_TASK_FILE = 'pm.task.task_file.created',
  DELETE_TASK_FILE = 'pm.task.task_file.deleted',

  SCREEN_RECORDER_UPLOAD_TRANSCODING_PROGRESS = 'screen_recorder.upload.transcoding_progress',
  SCREEN_RECORDER_UPLOAD_SUMMARY_GENERATED = 'screen_recorder.upload.summary_generated',

  HELP_DESK_MESSAGE_NEW = 'help_desk:message_new',
  HELP_DESK_MESSAGE_EDITED = 'help_desk:message_edited',
  HELP_DESK_MESSAGE_DELETED = 'help_desk:message_deleted',
  HELP_DESK_CHAT_STATUS_CHANGED = 'help_desk:chat_status_changed',
  HELP_DESK_CHAT_BLOCKED = 'help_desk:chat_blocked',
  HELP_DESK_CHAT_UNBLOCKED = 'help_desk:chat_unblocked',
  HELP_DESK_TYPING = 'help_desk:typing',
  HELP_DESK_PRESENCE = 'help_desk:presence',
  HELP_DESK_TICKET_UPDATED = 'help_desk:ticket_updated',
}

export interface IWsMessageUpdateStream {
  actions: IStreamThreadActionRoot[]
  message_thread: IStreamThreadInfo
}

export interface IWsMessageUpdateTaskOwner {
  task_id: number
  owner_id: Optional<number>
}

export interface IWsMessageDeleteTaskStageAssignee {
  task_id: number
  status_id: number
  assigned_user_id: number
}

export interface IWsMessageAddTaskStageAssignee {
  task_id: number
  status_id: number
  assigned_user_id: number
}

export interface IWsMessageReplaceTaskStageAssignee {
  task_id: number
  status_id: number
  assigned_user_id: number
  old_assigned_user_id: number
}

export interface IWsMessageReplaceTaskSubscriber {
  task_id: number
  user_id: number
  old_subscribed_user_id: number
}

export interface IWsMessageDeleteTaskSubscriber {
  task_id: number
  user_id: number
}

export interface IWsMessageAddTaskSubscriber {
  task_id: number
  user_id: number
}

export interface IWsMessageAddTaskTag {
  task_id: number
  tag: string
  color: number | null
  id: number
}

export interface IWsMessageUpdateTaskTag {
  task_tag: {
    id: number
    color: number | null
    name: string
  }
}

export interface IWsMessageDeleteTaskTag {
  task_id: number
  tag: string
  id: number
}

export interface IWsMessageUpdateTaskStage {
  task_id: number
  id: number
  status_id: number
  activated_at: Date
  activated_by_id: number
}

export interface IWsMessageUpdateTaskPriority {
  task_id: number
  priority: TaskPriorityTypes
}

export interface IWsMessageUpdateTaskPoint {
  task_id: number
  point_estimate: Optional<string>
}

export interface IWsMessageUpdateTaskDuration {
  task_id: number
  start_at: Date
  end_at: Date
}

export interface IWsMessageUpdateTaskTitle {
  task_id: number
  title: string
}

export interface IWsMessageCreateTaskSubtask {
  task_id: number
  subtask_group_id: number
  subtask: ITaskSubtask
}

export interface IWsMessageDeleteTaskSubtask {
  task_id: number
  subtask_group_id: number
  subtask_id: number
}

export interface IWsMessageChangeGroupTaskSubtask {
  task_id: number
  subtask_group_id: number
  subtask_id: number
  old_group_id: number
  new_group_subtask_ids: number[]
}

export interface IWsMessageDeleteTaskSubtaskGroup {
  task_id: number
  subtask_group_id: number
}

export interface IWsMessageRenameTaskSubtaskGroup {
  task_id: number
  subtask_group_id: number
  old_name: string
  new_name: string
}

export interface IWsMessageCreateTaskSubtaskGroup {
  position: number
  task_id: number
  subtask_group: ITaskSubtaskGroup
  task_block_sorting: ITaskBlocksSortingItem[]
}

export interface IWsMessageCreateTask {
  task: ITask
}

export interface IWsMessageDeleteTask {
  task_id: number
}

export interface IWsMessageUpdateTaskSubtaskAssignee {
  task_id: number
  subtask_group_id: number
  subtask_id: number
  assigned_user_id: number
  activated_by_id: number
}

export interface IWsMessageUpdateTaskSubtaskTitle {
  task_id: number
  subtask_group_id: number
  subtask_id: number
  title: string
}

export interface IWsMessageUpdateTaskSubtaskStatus {
  task_id: number
  subtask_group_id: number
  subtask_id: number
  subtask_status_id: number
  activated_at: Date
  activated_by_id: number
  assigned_user_id?: number
}

export interface IWsMessageCreateTaskTextBlock {
  task_id: number
  text_block: ITextBlock
  position: number
  task_block_sorting: ITaskBlocksSortingItem[]
}

export interface IWsMessageCreateTaskTextBlocks {
  task_id: number
  text_blocks: ITextBlock[]
  task_block_sortings: {
    id: number //block id
    task_block_sorting: ITaskBlocksSortingItem
  }[]
}

export interface IWsMessageUpdateTaskTextBlock {
  task_id: number
  text_block: ITextBlock
}

export interface IWsMessageDeleteTaskTextBlock {
  task_id: number
  text_block_id: number
}

export interface IWsMessage<T> {
  metadata: {
    user_ids: number[]
    organization_id: number
    project_id: number
    triggered_by_id: number
    triggered_at: Date
    command_guid: string
    event: WsMessageEventTypes
  }
  data: T
}

export interface IWsMessageLibrary<T> {
  data: T
  metadata: {
    command_guid: string | null
    event: WsMessageEventTypes
    organization_id: number
    triggered_by_id: number
    triggered_at: Date
    upload_uuid: string
  }
}

export interface IWsMessageCreateProject {
  project: IProject
}

export interface IWsMessageUpdateProject {
  project: IProject
}

export interface IWsMessageDeleteProject {
  project_id: number
}

export interface IWsMessageCreateProjectRole {
  project_role: IProjectRole
}

export interface IWsMessageUpdateProjectRole {
  project_role: IProjectRole
}

export interface IWsMessageDeleteProjectRole {
  project_role_id: number
}

export interface IWsMessageBlockedSubtask {
  blocking_sources: IBlockingSource[]
  description: string
  subtask_group_id: number
  subtask_id: number
  task_id: number
}

export interface IWsMessageUnblockedSubtask {
  subtask_group_id: number
  subtask_id: number
  task_id: number
  work_blocking_id: number
}

export interface IWsMessageCreateTaskFile {
  task_block_sortings: ITaskBlocksSortingItem[]
  task_file: IFile
  task_id: number
  position: number
}

export interface IWsMessageDeleteTaskFile {
  task_file: number
  task_id: number
}

export interface IWsMessageTaskBlocksSorted {
  task_block_sortings: ITaskBlocksSortingItem[]
  task_id: number
}

export interface IWsMessageUpdateVideoTranscodingProgress {
  big_preview_url: string
  progress: number
  small_preview_url: string
  state: string
  type: UploadVersionType
}

export interface IWsMessageChangeUploadTitle {
  upload_title: string
}

// --- Help desk (events are already defined in WsMessageEventTypes) ---

export interface IWsMessageHelpDeskMessageNew {
  chat_id: number
  message: IAdminChatMessage
}

export interface IWsMessageHelpDeskMessageEdited {
  chat_id: number
  message: IAdminChatMessage
}

export interface IWsMessageHelpDeskMessageDeleted {
  chat_id: number
  message_id: number
}

/** Объединённое событие смены статуса или владельца чата. */
export interface IWsMessageHelpDeskChatStatusChanged {
  chat_id: number
  status: ChatStatus
  owner_agent_id: number | null
}

export interface IWsMessageHelpDeskChatBlocked {
  chat_id: number
  block: IChatBlock
}

export interface IWsMessageHelpDeskChatUnblocked {
  chat_id: number
}

export interface IWsMessageHelpDeskTyping {
  chat_id: number
  user_id: number
  side: ChatMessageSide
}

export interface IWsMessageHelpDeskPresence {
  chat_id: number
  agent_id: number
  active: boolean
}

/** Универсальный сигнал "тикет изменился" — фронт подтягивает свежие данные запросом. */
export interface IWsMessageHelpDeskTicketUpdated {
  ticket_id: number
}
